<template>
<div class="chapter">
<div class="inner_page">
<h1 class="chap-title" style="padding-top:2em"><a id="pageix"></a><a href="#/006_Contents#pre" id="pre"><span style="border-top:5px solid #808080;padding-top:0.2em">PREFACE</span></a></h1>
<p class="paraindent1">Full-scale introduction of diesel-powered locomotives into the American railway industry
occurred primarily during the fifteen years following World War II. Although this is a
relatively short time for such a fundamental change, the actual gestation period of this
revolutionary technology had taken much longer, as one would anticipate. Building on the
highly documented segments of this significant sequence of events, the following narrative
presents the evolutionary highlights of dieselization without delving into excessive details
concerning each builder, each railroad, or each locomotive model. Moreover, the presentation
not only includes technical aspects but also comments on the economic and social
ramifications.</p>
<p class="paraindent1">Clearly, these revolutionary machines evolved via the traditional trial-and-error melding
of rudimentary concepts from disparate sources, followed by the absorption of periodic
refinements of both electrical and mechanical elements, to produce a machine so powerful
that it led to larger freight cars, stronger couplers, and more robust roadbeds. This
enabled American railroads to continually set new world standards for Gross Ton-Miles per
Train Hour, symbolic of the aggregate power produced by moving heavy trains at high
speeds. Such results may well have been part of the motivation for the bold statement by a
prominent rail commentator in March 2004 that the diesel revolution was “the greatest
technological change of the twentieth century.” While many would say that this is an
exaggeration, others would agree that this was certainly one of the century’s most important
technical changes.</p>
<p class="paraindent1">This book will follow the general approach used in my companion volume, <i>Perfecting
the American Steam Locomotive</i> (Indiana University Press, 2003). Thus there will be emphasis
on the role of leading engineers whose innovations paved the way for critical
breakthroughs. Unlike steam locomotive evolution, which occurred in parallel with development
of the nation’s railway network, introduction of diesel power represented a
revolutionary upheaval for an established sector of the economy that sent shock waves
through rigid corporate cultures and staid government regulators. To some it represented
the promise of enormous profits from rail operations, while to others it was an unmitigated
disaster that personified the oft-repeated plight of business enterprises, namely, dealing
with the introduction of a labor-saving technology that carried with it harsh consequences
for many of its employees.</p>
<p class="paraindent1">A recurrent theme in technological evolution is incrementalism, with progress on
various components occurring simultaneously in many locations. Thus an understanding <a id="pagex">of these precursor (or enabling) technologies is extremely important. For diesel locomotives,
there were separate developments early in the twentieth century on three key technical
areas: a lightweight and efficient diesel engine, an effective electric-drive system, and a
control system to modulate the power train. Only after these subsystems had reached a
certain level of sophistication could the important process of integrating them into a
commercially successful locomotive be undertaken.</a></p><a id="pagex">
<p class="paraindent1">The coverage will consider five developmental periods of the twentieth century.</p>
<p class="paranoindent1" style="padding-left:2em;text-indent:-1em;margin-top:1em;"><i>Internal combustion powered coaches</i>—These used mostly gasoline engines, but power
to the wheels was usually by direct current electricity.</p>
<p class="paranoindent1" style="padding-left:2em;text-indent:-1em;"><i>Early diesel-electric locomotives</i>—Eight large companies built and marketed diesel locomotives
before 1950, but only two had emerged by 1970 to advance this technology to
a higher level.</p>
<p class="paranoindent1" style="padding-left:2em;text-indent:-1em;"><i>Maturation period</i>—The two major builders competed fiercely by offering continually
improved designs for direct current locomotives, increasing power steadily to meet
industry demands for higher speeds and longer trains.</p>
<p class="paranoindent1" style="padding-left:2em;text-indent:-1em;"><i>Electrical revolution</i>—Technical advances in solid-state electronics after 1960 permitted
development of new devices that produced alternating current propulsion by 1993 as
a more efficient alternative to traditional DC drive.</p>
<p class="paranoindent1" style="padding-left:2em;text-indent:-1em;"><i>Manufacturing turnaround</i>—In one of the greatest reversals in American industrial
history, the last decade of the century saw an industry pioneer, and the leading diesel
builder for over five decades, relegated to second place by an even older American
company.</p>
<p class="paraindent1" style="margin-top:1em;">Proper coverage of such a broad subject requires the assistance of numerous colleagues
and associates. I wish to express my deep appreciation to the following group of fellow rail
enthusiasts. Especially useful were instructional manuals for early diesel units used by rail
lines for their locomotive operators. These were supplied by Ed Mims, David Orr, and
David Price, the first two of whom are former railroad executives. Reviewing early drafts of
the manuscript to improve its accuracy and clarity was a time-consuming task that was
carried out enthusiastically and successfully by James Mischke, a leading archivist of diesel
history, and Mark Reutter, a widely published historian of American heavy industry. An
extremely valuable contributor was my former colleague at the University of Texas, Mack
Grady, an expert on power electronics, which represented the enabling technology for the
realization of AC-drive locomotives.</p>
<p class="paraindent1">To assist with illustrations, I was most fortunate to enlist some of America’s leading
photographers and photo collectors. From Arizona came historical Southern Pacific images
from noted collector Arnold Menke, while Jim Shaughnessy of New York provided
details of the <i>Flying Yankee</i> restoration and the Delaware &amp; Hudson PA’s. From the
Midwest came the assistance of Kevin Keefe and the David P. Morgan Library of Ka-lmbach
Publishing, as well as important additions from the vast collection of Louis Marre,
who has archived and written about diesels for over four decades. A trio of my longtime
friends from the Southeast, Jerry Lachaussee, David Price, and Louis Saillard, also provided
key illustrations for this book as they did for the steam volume. In addition, Messrs.
Mims and Mischke solved critical photo needs, while images of contemporary diesels
came from fellow Texans Ted Ferkenhoff and Chris Palmieri along with Bill McCoy of
Florida and Beth Krueger of Montana. A significant contribution was the artistic talent of
Tony Howe, producer of the line drawings. As a computer expert, he assisted me and David
Price with many of the digital renditions of antique images.</p>
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 007_Preface.vue?717a2158